import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { getApiUrl } from "../../../../../../config/api";

// ── Add-Competitor Modal ──────────────────────────────────────────────────────
function AddCompetitorModal({ language, open, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setName("");
            setError("");
            setSaving(false);
        }
    }, [open]);

    const handleCreate = async () => {
        if (!name.trim()) return;
        setSaving(true);
        setError("");
        try {
            const token = await (
                await import("../../../../../../firebase/firebase")
            ).auth.currentUser?.getIdToken();
            await axios.post(
                `${getApiUrl()}/api/v2/competitors/`,
                { competitor_string: name.trim() },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            onCreated();
            onClose();
        } catch (err) {
            setError(
                err.response?.data?.error ||
                    (language === "zh"
                        ? "建立失敗，請再試一次。"
                        : "Creation failed. Please try again."),
            );
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl animate-slideUp">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                >
                    <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>
                <div className="p-6">
                    <div className="w-12 h-12 bg-orange-500/10 rounded-2xl flex items-center justify-center mb-4 text-orange-500">
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M12 4v16m8-8H4"
                            />
                        </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                        {language === "zh"
                            ? "新增競品廠商"
                            : "Add New Competitor"}
                    </h3>
                    <p className="text-sm text-gray-500 mb-6">
                        {language === "zh"
                            ? "輸入競品廠商名稱。"
                            : "Enter the competitor / brand name."}
                    </p>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                            {language === "zh" ? "廠商名稱" : "Competitor Name"}
                            <span className="text-red-400 ml-0.5">*</span>
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={
                                language === "zh"
                                    ? "例：Laird Technologies"
                                    : "e.g. Laird Technologies"
                            }
                            className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all"
                        />
                    </div>
                    {error && (
                        <div className="mt-4 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                            {error}
                        </div>
                    )}
                    <div className="flex items-center justify-end gap-3 mt-6">
                        <button
                            onClick={onClose}
                            disabled={saving}
                            className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                        >
                            {language === "zh" ? "取消" : "Cancel"}
                        </button>
                        <button
                            onClick={handleCreate}
                            disabled={saving || !name.trim()}
                            className={`px-6 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 ${saving || !name.trim() ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-orange-500 text-white hover:bg-orange-600 shadow-md shadow-orange-500/20"}`}
                        >
                            {saving ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    {language === "zh"
                                        ? "建立中…"
                                        : "Creating…"}
                                </>
                            ) : (
                                <>{language === "zh" ? "建立" : "Create"}</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
function CompetitorSelection({ language, currentUser, onBack, onNext }) {
    const [competitors, setCompetitors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState("");

    const [selectedCompetitor, setSelectedCompetitor] = useState(null);

    const [showAddCompetitor, setShowAddCompetitor] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setFetchError("");
        try {
            const token = await currentUser.getIdToken();
            const headers = { Authorization: `Bearer ${token}` };
            const [compRes] = await Promise.all([
                axios.get(`${getApiUrl()}/api/v2/competitors/`, { headers }),
            ]);
            setCompetitors(compRes.data?.data || []);
        } catch {
            setFetchError(
                language === "zh"
                    ? "無法載入資料，請稍後再試。"
                    : "Failed to load data. Please try again.",
            );
        } finally {
            setLoading(false);
        }
    }, [currentUser, language]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const canProceed = selectedCompetitor !== null;

    const handleNext = () => {
        if (!canProceed) return;
        onNext({
            producerId: selectedCompetitor.competitor_id,
            producerName: selectedCompetitor.competitor_string,
        });
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
                <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-gray-500 text-sm">
                    {language === "zh" ? "載入中…" : "Loading…"}
                </p>
            </div>
        );
    }

    if (fetchError) {
        return (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
                <p className="text-red-500 text-sm">{fetchError}</p>
                <button
                    onClick={fetchData}
                    className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                    {language === "zh" ? "重試" : "Retry"}
                </button>
            </div>
        );
    }

    return (
        <div className="animate-slideUp flex justify-center">
            <div className="w-full max-w-4xl">
                {/* Header row */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onBack}
                            className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                        </button>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                {language === "zh"
                                    ? "選擇廠商與類別"
                                    : "Select Competitor & Category"}
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-300 mt-0.5">
                                {language === "zh"
                                    ? "請先選擇競品廠商與類別，再上傳 PDF"
                                    : "Choose a competitor and category before uploading the PDF"}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Step 1 — Competitor */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            {language === "zh"
                                ? "步驟 1 — 選擇競品廠商"
                                : "Step 1 — Select Competitor"}
                        </p>
                        {/* <button
                            onClick={() => setShowAddCompetitor(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-orange-600 bg-orange-500/10 hover:bg-orange-500/20 border-2 border-orange-500/20 hover:border-orange-500/40 rounded-xl transition-all"
                        >
                            <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4v16m8-8H4"
                                />
                            </svg>
                            {language === "zh" ? "新增廠商" : "New Competitor"}
                        </button> */}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {competitors.map((c) => {
                            const isSelected =
                                selectedCompetitor?.competitor_id ===
                                c.competitor_id;
                            return (
                                <button
                                    key={c.competitor_id}
                                    onClick={() => setSelectedCompetitor(c)}
                                    className={`group bg-white border-2 rounded-2xl p-6 text-left transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 hover:ring-4 focus:outline-none ${
                                        isSelected
                                            ? "border-orange-500/50 ring-4 ring-orange-500/20 shadow-lg shadow-orange-500/10"
                                            : "border-gray-200 hover:border-orange-500/50 hover:ring-orange-500/20"
                                    }`}
                                >
                                    <div
                                        className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 ${isSelected ? "bg-orange-500 text-white" : "bg-orange-500/10 text-orange-500"}`}
                                    >
                                        <svg
                                            className="w-6 h-6"
                                            fill={`${isSelected ? "#ffffffff" : "#ff5500ff"}`}
                                            viewBox="-2 0 16 16"
                                            id="company-16px"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <g
                                                id="SVGRepo_bgCarrier"
                                                strokeWidth="0"
                                            ></g>
                                            <g
                                                id="SVGRepo_tracerCarrier"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            ></g>
                                            <g id="SVGRepo_iconCarrier">
                                                {" "}
                                                <path
                                                    id="Path_133"
                                                    data-name="Path 133"
                                                    d="M323.5-192h-9a1.5,1.5,0,0,0-1.5,1.5V-176h12v-14.5A1.5,1.5,0,0,0,323.5-192ZM318-177v-3h2v3Zm6,0h-3v-3.5a.5.5,0,0,0-.5-.5h-3a.5.5,0,0,0-.5.5v3.5h-3v-13.5a.5.5,0,0,1,.5-.5h9a.5.5,0,0,1,.5.5Zm-8-12h2v2h-2Zm4,0h2v2h-2Zm-4,4h2v2h-2Zm4,0h2v2h-2Z"
                                                    transform="translate(-313 192)"
                                                ></path>{" "}
                                            </g>
                                        </svg>
                                    </div>
                                    <h3
                                        className={`text-sm font-bold mb-1 ${isSelected ? "text-orange-600" : "text-gray-900"}`}
                                    >
                                        {c.competitor_string}
                                    </h3>
                                    {isSelected && (
                                        <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-orange-500">
                                            <svg
                                                className="w-4 h-4"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                            {language === "zh"
                                                ? "已選擇"
                                                : "Selected"}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-between pt-2">
                    <button
                        onClick={onBack}
                        className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                    >
                        {language === "zh" ? "返回" : "Back"}
                    </button>
                    <button
                        onClick={handleNext}
                        disabled={!canProceed}
                        className={`px-6 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 ${
                            canProceed
                                ? "bg-orange-500 text-white hover:bg-orange-600 shadow-md shadow-orange-500/20 hover:shadow-lg hover:shadow-orange-500/30"
                                : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        }`}
                    >
                        {language === "zh"
                            ? "下一步：選擇類別"
                            : "Next: Select Category"}
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            <AddCompetitorModal
                language={language}
                open={showAddCompetitor}
                onClose={() => setShowAddCompetitor(false)}
                onCreated={() => {
                    setSelectedCompetitor(null);
                    fetchData();
                }}
            />
        </div>
    );
}

export default CompetitorSelection;
